import { RefObject, createContext, useContext, useEffect, useState } from 'react'

import { getRefDimension } from '../../../../utils/util'
import useIsScrollingUp from '../../../hooks/useIsScrollingUp'

export const HeaderContext = createContext<RefObject<HTMLElement> | null>(null)

// Hide header while scrolling down and condition param is true
// Returns boolean value based on if header is hidden or not
export const useHideHeaderOnScrollCondition = (
  condition: boolean,
  customHeaderHeight?: number,
): boolean => {
  const isScrollingUp = useIsScrollingUp()
  const headerRef = useContext(HeaderContext)

  const headerHeight = customHeaderHeight || (headerRef?.current?.getBoundingClientRect && getRefDimension('height', headerRef))

  // Hide header if under product navbar and scrolling down
  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(false)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (!isScrollingUp && condition && currentScrollY > 100 && headerRef?.current) {
        // Hide header only if scrolled down more than 100px to mitigate iphone rubber band scroll
        headerRef.current.style.transform = `translate3d(0px, -${headerHeight}px, 0px)`
        setIsHeaderHidden(true)
      } else if (headerRef?.current?.style) {
        // Show header
        headerRef.current.style.transform = ''
        setIsHeaderHidden(false)
      }
    }

    // Initial check
    handleScroll()

    // Add scroll listener for real-time updates
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (headerRef?.current?.style) headerRef.current.style.transform = ''
      setIsHeaderHidden(false)
    }
  }, [isScrollingUp, customHeaderHeight, condition, headerHeight])

  return isHeaderHidden
}

export const useHeaderRef = () => useContext(HeaderContext)
